import type { TraitLists } from '../interfaces/CourtInterfaces';

interface CourtDto {
  Court_guid: string;
  Court_Email: string;
  Court_Address: string;
  Court_Name: string;
  // Sent by CourtController and declared on the other CourtDto in interfaces/CourtInterfaces.ts,
  // but missing here, so the cases list could not read a county the server was already returning.
  Court_County?: string | null;
  Court_Phone?: string;
  Court_Fax?: string;
  Court_Active: boolean;
}

interface FullCourtDto extends CourtDto {
  prosecutors: {
    Prosecutor_guid: string;
    Prosecutor_Email: string;
    Prosecutor_Name: string;
    Prosecutor_Active: boolean;
  }[];
}

interface CourtListResponse {
  message: string;
  courts: FullCourtDto[];
  pagination: {
    from: number;
    size: number;
    total: number;
  };
}

export interface CourtClientDto {
  ticketGuid?: string;
  userGuid?: string | null;
  clientName?: string;
  citationNumber?: string | null;
  dateOfBirth?: string | null;
  courtDate?: string | null;
}

export interface CourtClientsDto {
  clients?: CourtClientDto[];
  total?: number;
  missingCitation?: number;
  missingDateOfBirth?: number;
}

class CourtApi {
  static async searchCourts(params: {
    from?: number;
    size?: number;
    Court_Name?: string;
    Court_Address?: string;
    Court_Email?: string;
    Court_Phone?: string;
    Court_Fax?: string;
    Court_Active?: boolean;
    Court_guid?: string;
    search?: string;
  }): Promise<CourtListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params.from !== undefined) queryParams.append('from', params.from.toString());
    if (params.size !== undefined) queryParams.append('size', params.size.toString());
    if (params.Court_Name) queryParams.append('Court_Name', params.Court_Name);
    if (params.Court_Address) queryParams.append('Court_Address', params.Court_Address);
    if (params.Court_Email) queryParams.append('Court_Email', params.Court_Email);
    if (params.Court_Phone) queryParams.append('Court_Phone', params.Court_Phone);
    if (params.Court_Fax) queryParams.append('Court_Fax', params.Court_Fax);
    if (params.Court_Active !== undefined) queryParams.append('Court_Active', params.Court_Active.toString());
    if (params.Court_guid) queryParams.append('Court_guid', params.Court_guid);
    if (params.search) queryParams.append('search', params.search);

    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court/list?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch courts' }));
      throw new Error(errorData.message || 'Failed to fetch courts');
    }

    return await response.json();
  }

  static async getCourtById(guid: string): Promise<{ message: string; court: FullCourtDto }> {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court/${guid}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Failed to fetch court' }));
      throw new Error(errorData.message || 'Failed to fetch court');
    }

    return await response.json();
  }

  /**
   * Who the firm has in front of this court (Lloyd, 16 Sep 2026).
   *
   * `total` counts only cases attached to THIS court. Most cases are attached to no court at all,
   * which is why the screen says so rather than presenting this as the whole docket.
   */
  static async getClientsAtCourt(guid: string): Promise<CourtClientsDto> {
    const response = await fetch(
      `${process.env.API_HOST || ''}/api/v1/court/${guid}/clients`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }
    );

    if (!response.ok) {
      throw new Error('Failed to load the clients at this court');
    }

    return await response.json();
  }

  /** Lloyd's rating lists (6 Oct 2026), served from the backend's one definition. */
  static async getTraits(): Promise<TraitLists> {
    const response = await fetch(`${process.env.API_HOST || ''}/api/v1/court/traits`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to load the court and prosecutor ratings');
    }

    return await response.json();
  }
}

export default CourtApi;
export type { CourtDto, FullCourtDto, CourtListResponse };
